import { ActionIcon, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { Trash } from '@phosphor-icons/react';
import { useState, type FC } from 'react';

import { deleteUserAlert } from '@api/userAlerts';
import type { AlertItem } from '@components/TodoChat/types';

/**
 * Removes a scheduled alert from Home: the alert definition goes, so its schedule stops and
 * no further cards are written. Confirms first; the card hides itself on success.
 */
const DeleteAlertButton: FC<{ alert: AlertItem; className?: string; onDeleted: () => void }> = ({ alert, className, onDeleted }) => {
    const [busy, setBusy] = useState(false);
    const alertId = alert.relatedEntityId;
    if (!alertId) return null;

    const confirm = () =>
        modals.openConfirmModal({
            title: 'Remove this scheduled alert?',
            children: `"${alert.title}" will stop running and leave the Home screen. You can schedule it again from its page.`,
            labels: { confirm: 'Remove', cancel: 'Keep' },
            confirmProps: { color: 'red', size: 'xs' },
            cancelProps: { size: 'xs' },
            onConfirm: () => {
                setBusy(true);
                deleteUserAlert(alertId)
                    .then(() => {
                        notifications.show({ color: 'gray', message: 'Alert removed.' });
                        onDeleted();
                    })
                    .catch(() => notifications.show({ color: 'red', message: 'Could not remove the alert.' }))
                    .finally(() => setBusy(false));
            },
        });

    return (
        <Tooltip label="Remove this alert" withArrow>
            <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label="Remove this alert"
                className={className}
                loading={busy}
                onClick={(e) => {
                    e.stopPropagation();
                    confirm();
                }}
            >
                <Trash size={15} />
            </ActionIcon>
        </Tooltip>
    );
};

export default DeleteAlertButton;
